import { notificationActions } from "actions";
import {
  SUBMISSION_STATE_CANCELLED,
  SUBMISSION_STATE_DISCUSSION,
  SUBMISSION_STATE_PROSPECT,
  SUBMISSION_STATE_WON,
} from "constants/dbValues";
import MaterialTable, { MTableBody } from "@material-table/core";
import TablePagination from "@material-ui/core/TablePagination";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { statsService, submissionService } from "services";
import { dateTimeFormat, materialTableProps } from "utils";
import NotificationsList from "./NotificationsList";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import SubmissionsGraphs from "./SubmissionsGraphs";
import Header from "./_parts/Header";
import { Horloge } from "components";
import { Icon, Spin } from "antd";
import { icons } from "constants/icons";
import { connect } from "react-redux";
import { mapDispatchToProps, mapStateToProps } from "./_parts/SideNav";
import { useHistory, useLocation, withRouter } from "react-router-dom";
import { history } from "utils";

const RADIAN = Math.PI / 180;

const ELLIPSIS = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  maxWidth: "180px",
  textOverflow: "ellipsis",
};

const ELLIPSIS_SMALL = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  maxWidth: "120px",
  textOverflow: "ellipsis",
};

const ELLIPSIS_SMALLER = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  maxWidth: "80px",
  textOverflow: "ellipsis",
};

const PIE_COLORS = ["#066341", "#535353"];

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  index,
  value,
}) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 1.75;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill={PIE_COLORS[index]}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
    >
      {value}
    </text>
  );
};

const COLUMNS_ON_GOING = [
  {
    field: "creator",
    title: "Créateur",
    render: (rowData) => (rowData.user ? rowData.user.name : ""),
    orderField: "user.name",
    type: "string",
    sorting: false,
    customFilterAndSearch: (term, rowData) => {
      return rowData.user ? rowData.user.name.toUpperCase().includes(term.toUpperCase()) : false;
    },
    // cellStyle: CS_SHORT,
  },
  {
    field: "num",
    title: "No. soumission",
    type: "string",
    cellStyle: ELLIPSIS_SMALLER,
    defaultSort: "desc",
    // sorting: true,
  },
  {
    field: "client_id",
    title: "Client",
    render: (rowData) => (rowData.client ? rowData.client.name : ""),
    orderField: "clients.name",
    type: "string",
    cellStyle: ELLIPSIS_SMALL,
    customFilterAndSearch: (term, rowData) => {
      return rowData.client
        ? rowData.client.name.toUpperCase().includes(term.toUpperCase())
        : false;
    },
  },
  {
    field: "title",
    title: "Titre",
    type: "string",
  },
  {
    field: "date_visite",
    title: "Date de visite",
    render: (rowData) =>
      rowData.date_visite ? moment(rowData.date_visite).format(dateTimeFormat) : "",
    type: "date",
    filterPlaceholder: "De // À (JJ-MM-AAAA)",
    cellStyle: ELLIPSIS,
    customFilterAndSearch: (term, rowData) => {
      return rowData.date_visite
        ? moment(rowData.date_visite).format(dateTimeFormat).includes(term)
        : false;
    },
  },
  {
    field: "date_depot",
    title: "Date de dépot",
    render: (rowData) =>
      rowData.date_depot ? moment(rowData.date_depot).format(dateTimeFormat) : "",
    type: "date",
    filterPlaceholder: "De // À (JJ-MM-AAAA)",
    cellStyle: ELLIPSIS,
    customFilterAndSearch: (term, rowData) => {
      return rowData.date_depot
        ? moment(rowData.date_depot).format(dateTimeFormat).includes(term)
        : false;
    },
  },
];

const COLUMNS_WAITING = [
  {
    field: "creator",
    title: "Créateur",
    render: (rowData) => (rowData.user ? rowData.user.name : ""),
    orderField: "user.name",
    type: "string",
    sorting: false,
    customFilterAndSearch: (term, rowData) => {
      return rowData.user ? rowData.user.name.toUpperCase().includes(term.toUpperCase()) : false;
    },
    // cellStyle: CS_SHORT,
  },
  {
    field: "num",
    title: "No. soumission",
    type: "string",
    cellStyle: ELLIPSIS_SMALLER,
    defaultSort: "desc",
    // sorting: true,
  },
  {
    field: "client_id",
    title: "Client",
    render: (rowData) => (rowData.client ? rowData.client.name : ""),
    orderField: "clients.name",
    type: "string",
    cellStyle: ELLIPSIS_SMALL,
    customFilterAndSearch: (term, rowData) => {
      return rowData.client
        ? rowData.client.name.toUpperCase().includes(term.toUpperCase())
        : false;
    },
  },
  {
    field: "title",
    title: "Titre",
    type: "string",
  },
  {
    field: "date_visite",
    title: "Date de visite",
    render: (rowData) =>
      rowData.date_visite ? moment(rowData.date_visite).format(dateTimeFormat) : "",
    type: "date",
    filterPlaceholder: "De // À (JJ-MM-AAAA)",
    cellStyle: ELLIPSIS,
    customFilterAndSearch: (term, rowData) => {
      return rowData.date_visite
        ? moment(rowData.date_visite).format(dateTimeFormat).includes(term)
        : false;
    },
  },
  {
    field: "date_depot",
    title: "Date de dépot",
    render: (rowData) =>
      rowData.date_depot ? moment(rowData.date_depot).format(dateTimeFormat) : "",
    type: "date",
    filterPlaceholder: "De // À (JJ-MM-AAAA)",
    cellStyle: ELLIPSIS,
    customFilterAndSearch: (term, rowData) => {
      return rowData.date_depot
        ? moment(rowData.date_depot).format(dateTimeFormat).includes(term)
        : false;
    },
  },
];

const COLUMNS_DISCUSSION = COLUMNS_WAITING.map((column) => ({ ...column }));

const COLUMNS_NOT_WON = [
  {
    field: "creator",
    title: "Créateur",
    render: (rowData) => (rowData.user ? rowData.user.name : ""),
    orderField: "user.name",
    type: "string",
    sorting: false,
    customFilterAndSearch: (term, rowData) => {
      return rowData.user ? rowData.user.name.toUpperCase().includes(term.toUpperCase()) : false;
    },
    // cellStyle: CS_SHORT,
  },
  {
    field: "num",
    title: "No. soumission",
    type: "string",
    cellStyle: ELLIPSIS_SMALLER,
    defaultSort: "desc",
    // sorting: true,
  },
  {
    field: "client_id",
    title: "Client",
    render: (rowData) => (rowData.client ? rowData.client.name : ""),
    orderField: "clients.name",
    type: "string",
    cellStyle: ELLIPSIS_SMALL,
    customFilterAndSearch: (term, rowData) => {
      return rowData.client
        ? rowData.client.name.toUpperCase().includes(term.toUpperCase())
        : false;
    },
  },
  {
    field: "title",
    title: "Titre",
    type: "string",
  },
  {
    field: "date_visite",
    title: "Date de visite",
    render: (rowData) =>
      rowData.date_visite ? moment(rowData.date_visite).format(dateTimeFormat) : "",
    type: "date",
    filterPlaceholder: "De // À (JJ-MM-AAAA)",
    cellStyle: ELLIPSIS,
    customFilterAndSearch: (term, rowData) => {
      return rowData.date_visite
        ? moment(rowData.date_visite).format(dateTimeFormat).includes(term)
        : false;
    },
  },
  {
    field: "date_depot",
    title: "Date de dépot",
    render: (rowData) =>
      rowData.date_depot ? moment(rowData.date_depot).format(dateTimeFormat) : "",
    type: "date",
    filterPlaceholder: "De // À (JJ-MM-AAAA)",
    cellStyle: ELLIPSIS,
    customFilterAndSearch: (term, rowData) => {
      return rowData.date_depot
        ? moment(rowData.date_depot).format(dateTimeFormat).includes(term)
        : false;
    },
  },
];

const Pagination = ({ onChangeRowsPerPage, onChangePage, ...props }) => (
  <TablePagination
    {...props}
    onRowsPerPageChange={onChangeRowsPerPage}
    onPageChange={onChangePage}
  />
);

const handleFilterChange = (action, column, value) => {
  const type = "submissions";
  const filters = JSON.parse(localStorage.getItem(`${action}[${type}]`));
  const updated = { ...filters, [column]: value };
  localStorage.setItem(`${action}[${type}]`, JSON.stringify(updated));
};

const SubmissionsList = (props) => {
  const { notifications, notifier } = props;
  const historyHook = useHistory();
  const location = useLocation();

  const getInitialPageSize = (key, defaultValue) => {
    const value = parseInt(new URLSearchParams(location.search).get(key));
    return isNaN(value) ? defaultValue : value;
  };

  const updatePageSizeInUrl = (key, value) => {
    const params = new URLSearchParams(historyHook.location.search);
    params.set(key, value);
    historyHook.push({ search: params.toString() });
  };

  const [pageSizeOnGoing, setPageSizeOnGoing] = useState(() => getInitialPageSize("ps_on_going", 15));
  const [pageSizeWaiting, setPageSizeWaiting] = useState(() => getInitialPageSize("ps_waiting", 20));
  const [pageSizeDiscussion, setPageSizeDiscussion] = useState(() =>
    getInitialPageSize("ps_discussion", 10),
  );
  const [pageSizeNotWon, setPageSizeNotWon] = useState(() => getInitialPageSize("ps_not_won", 5));

  const [onGoing, setOnGoing] = useState([]);
  const [waiting, setWaiting] = useState([]);
  const [discussion, setDiscussion] = useState([]);
  const [notWon, setNotWon] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState(undefined);
  const [tableloading, setTableLoading] = useState(true);

  const onGoingRef = useRef(null);
  const waitingRef = useRef(null);
  const discussionRef = useRef(null);
  const notWonRef = useRef(null);

  const fetchSubmissions = async () => {
    const { submissions } = await submissionService.listSubmissions(
      {},
      {
        date_depot: [moment().subtract(32, "days").toISOString(), moment().toISOString()],
      },
      [SUBMISSION_STATE_PROSPECT, SUBMISSION_STATE_WON, SUBMISSION_STATE_CANCELLED],
    );
    return submissions;
  };
  const fetchCounts = async () => {
    const response = await statsService.get("/statistics/submission/", {
      indicator: "count",
    });
    setCounts(response);
  };

  useEffect(() => {
    sessionStorage.removeItem("gb");
    let user = localStorage.getItem("user");
    user = user ? JSON.parse(user) : null;
    setUser(user);
    if (!counts) {
      fetchCounts();
    }
    setLoading(false);
    fetchSubmissions()
      .then((submissions) => {
        if (submissions) {
          setOnGoing(submissions.filter((sub) => sub.submission_state.id === 2));
          setWaiting(submissions.filter((sub) => sub.submission_state.id === 3));
          setDiscussion(
            submissions.filter((sub) => sub.submission_state.id === SUBMISSION_STATE_DISCUSSION),
          );
          setNotWon(submissions.filter((sub) => sub.submission_state.id === 5));
        }
      })
      .finally(() => {
        setTableLoading(false);
      });
    return () => {
      setOnGoing([]);
      setWaiting([]);
      setDiscussion([]);
      setNotWon([]);
      setLoading(true);
      setUser(null);
      setCounts(undefined);
      setTableLoading(true);
    };
  }, []);

  return (
    <>
      <Header title="Tableau de bord des soumissions" />
      <div className="submissions-list-container">
        {!loading && (
          <>
            <div className="dashestimation-layout left">
              <nav className="nav-left">
                {user && <h2>{user.name}</h2>}
                {counts && (
                  <div className="nav-graphs">
                    <div className="graph">
                      <ResponsiveContainer height="100%" width={250}>
                        <PieChart height="100%" width={250}>
                          <Pie
                            isAnimationActive={false}
                            data={[
                              {
                                name: "Soumissions cette annee",
                                value: counts.submission_this_year,
                              },
                              {
                                name: "Soumissions l'an passe",
                                value: counts.submission_prev_year,
                              },
                            ]}
                            dataKey="value"
                            innerRadius={10}
                            outerRadius={20}
                            labelLine={false}
                            label={renderCustomizedLabel}
                          >
                            {[
                              {
                                name: "Soumissions cette annee",
                                value: counts.submission_this_year,
                              },
                              {
                                name: "Soumissions l'an passe",
                                value: counts.submission_prev_year,
                              },
                            ].map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={PIE_COLORS[index]} />
                            ))}
                          </Pie>
                          {/* <Tooltip /> */}
                        </PieChart>
                      </ResponsiveContainer>
                      <p>Deposées</p>
                    </div>
                    <div className="graph-legend">
                      <div className="label">
                        <div className="green" />
                        <p>Cette année</p>
                      </div>
                      <div className="label">
                        <div className="grey" />
                        <p>L'an dernier</p>
                      </div>
                    </div>
                    <div className="graph">
                      <ResponsiveContainer height="100%" width={250}>
                        <PieChart height="100%" width={250}>
                          <Pie
                            isAnimationActive={false}
                            data={[
                              {
                                name: "Soumission obtenues cette annee",
                                value: counts.validate_sub_this_year,
                              },
                              {
                                name: "Soumission obtenues l'an passe",
                                value: counts.validate_sub_prev_year,
                              },
                            ]}
                            dataKey="value"
                            cx="50%"
                            cy="50%"
                            innerRadius={10}
                            outerRadius={20}
                            labelLine={false}
                            label={renderCustomizedLabel}
                          >
                            {[
                              {
                                name: "Soumission obtenues cette annee",
                                value: counts.validate_sub_this_year,
                              },
                              {
                                name: "Soumission obtenues l'an passe",
                                value: counts.validate_sub_prev_year,
                              },
                            ].map((entry, index) => (
                              <Cell key={`cell-${index}-2`} fill={PIE_COLORS[index]} />
                            ))}
                          </Pie>
                          {/* <Tooltip /> */}
                        </PieChart>
                      </ResponsiveContainer>
                      <p>Obtenues</p>
                    </div>
                  </div>
                )}
                <Icon type={icons.plus} onClick={() => history.push("/submissions/create")} />
              </nav>
              <div id="table-container" className="table-container">
                <Spin spinning={tableloading}>
                  <MaterialTable
                    key="EN COURS"
                    title="EN COURS"
                    tableRef={onGoingRef}
                    data={onGoing}
                    columns={COLUMNS_ON_GOING}
                    onRowClick={(e, data) => {
                      sessionStorage.setItem("gb", true);
                      history.push(`/submissions/${data.id}/edit${location.search}`);
                    }}
                    onChangeColumnHidden={(column, hidden) => {
                      handleFilterChange("hidden_columns", column.field, hidden);
                    }}
                    {...materialTableProps("EN COURS", COLUMNS_ON_GOING)}
                    onRowsPerPageChange={(size) => {
                      setPageSizeOnGoing(size);
                      updatePageSizeInUrl("ps_on_going", size);
                    }}
                    options={{
                      pageSize: pageSizeOnGoing,
                      columnsButton: true,
                      search: true,
                    }}
                    components={{
                      Pagination,
                      Body: (props) => (
                        <MTableBody
                          {...props}
                          onFilterChanged={(id, value) => {
                            props.onFilterChanged(id, value);
                            handleFilterChange("filters", props.columns[id].field, value);
                          }}
                        />
                      ),
                    }}
                    editable={{}}
                  />
                  <MaterialTable
                    key="EN ATTENTE"
                    title="EN ATTENTE"
                    tableRef={waitingRef}
                    columns={COLUMNS_WAITING}
                    data={waiting}
                    {...materialTableProps("EN ATTENTE", COLUMNS_WAITING)}
                    onRowClick={(e, data) => {
                      sessionStorage.setItem("gb", true);
                      history.push(`/submissions/${data.id}/edit${location.search}`);
                    }}
                    onChangeColumnHidden={(column, hidden) =>
                      handleFilterChange("hidden_columns", column.field, hidden)
                    }
                    onChangeRowsPerPage={(size) => {
                      setPageSizeWaiting(size);
                      updatePageSizeInUrl("ps_waiting", size);
                    }}
                    options={{
                      pageSize: pageSizeWaiting,
                      toolbar: true,
                      columnsButton: true,
                      actionsColumnIndex: 99,
                    }}
                    components={{
                      Pagination,
                      Body: (props) => (
                        <MTableBody
                          {...props}
                          onFilterChanged={(id, value) => {
                            props.onFilterChanged(id, value);
                            handleFilterChange("filters", props.columns[id].field, value);
                          }}
                        />
                      ),
                    }}
                    editable={{}}
                  />
                  <MaterialTable
                    key="EN DISCUSSION"
                    title="EN DISCUSSION"
                    tableRef={discussionRef}
                    columns={COLUMNS_DISCUSSION}
                    data={discussion}
                    {...materialTableProps("EN DISCUSSION", COLUMNS_DISCUSSION)}
                    onRowClick={(e, data) => {
                      sessionStorage.setItem("gb", true);
                      history.push(`/submissions/${data.id}/edit${location.search}`);
                    }}
                    onChangeColumnHidden={(column, hidden) =>
                      handleFilterChange("hidden_columns", column.field, hidden)
                    }
                    onChangeRowsPerPage={(size) => {
                      setPageSizeDiscussion(size);
                      updatePageSizeInUrl("ps_discussion", size);
                    }}
                    options={{
                      pageSize: pageSizeDiscussion,
                      toolbar: true,
                      columnsButton: true,
                      actionsColumnIndex: 99,
                    }}
                    components={{
                      Pagination,
                      Body: (props) => (
                        <MTableBody
                          {...props}
                          onFilterChanged={(id, value) => {
                            props.onFilterChanged(id, value);
                            handleFilterChange("filters", props.columns[id].field, value);
                          }}
                        />
                      ),
                    }}
                    editable={{}}
                  />
                  <MaterialTable
                    key="NON OBTENUS"
                    title="NON OBTENUS"
                    tableRef={notWonRef}
                    columns={COLUMNS_NOT_WON}
                    data={notWon}
                    {...materialTableProps("NON OBTENUS", COLUMNS_NOT_WON)}
                    onRowClick={(e, data) => {
                      sessionStorage.setItem("gb", true);
                      history.push(`/submissions/${data.id}/edit${location.search}`);
                    }}
                    onChangeColumnHidden={(column, hidden) =>
                      handleFilterChange("hidden_columns", column.field, hidden)
                    }
                    onChangeRowsPerPage={(size) => {
                      setPageSizeNotWon(size);
                      updatePageSizeInUrl("ps_not_won", size);
                    }}
                    options={{
                      pageSize: pageSizeNotWon,
                      toolbar: true,
                      columnsButton: true,
                      actionsColumnIndex: 99,
                    }}
                    components={{
                      Pagination,
                      Body: (props) => (
                        <MTableBody
                          {...props}
                          onFilterChanged={(id, value) => {
                            props.onFilterChanged(id, value);
                            handleFilterChange("filters", props.columns[id].field, value);
                          }}
                        />
                      ),
                    }}
                    editable={{}}
                  />
                </Spin>
              </div>
            </div>

            <div className="dashestimation-layout right">
              <nav className="nav-right">
                <Horloge onDashboard={true} />
              </nav>
              <NotificationsList
                dashboard
                // loading={loading}
                notifications={notifications}
                // fetch={notifier.fetch()}
                // onItemLinkClick={onItemLinkClick}
                // onItemHover={onNotificationSeen}
              />
              <SubmissionsGraphs />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(SubmissionsList));
